import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InspectionSection from './components/InspectionSection';
import ComparisonView from './components/ComparisonView';
import DamageReport from './components/DamageReport';
import returnInspectionService from '../../services/returnInspectionService';
import toastService from '../../components/ui/Toast';

const TrailerReturnInspection = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams?.get('bookingId');
  const ownerId = searchParams?.get('ownerId');
  const trailerListingId = searchParams?.get('trailerListingId');

  const [inspectionId, setInspectionId] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [inspectionData, setInspectionData] = useState({});
  const [damageReports, setDamageReports] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude,
            timestamp: new Date()?.toISOString()
          });
          setLocationVerified(true);
        },
        (error) => {
          console.error('GPS error:', error);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user && bookingId) {
      initializeInspection();
    }
  }, [isAuthenticated, user, bookingId]);

  const initializeInspection = async () => {
    try {
      const { data: existing } = await returnInspectionService?.getInspectionByBookingId(bookingId);
      if (existing) {
        setInspectionId(existing?.id);
        if (existing?.status === 'completed') {
          setInitError('Return inspection already completed for this booking.');
        }
      } else {
        const { data: newInspection, error } = await returnInspectionService?.createInspection(
          bookingId,
          user?.id,
          ownerId,
          trailerListingId
        );
        if (error) {
          console.error('Init inspection error:', error?.message);
        } else {
          setInspectionId(newInspection?.id);
          if (gpsLocation) {
            await returnInspectionService?.updateGpsLocation(
              newInspection?.id,
              gpsLocation?.latitude,
              gpsLocation?.longitude
            );
          }
        }
      }
    } catch (error) {
      console.error('Initialize inspection error:', error);
    }
  };

  const inspectionSections = [
    {
      id: 'exterior',
      title: 'Exterior Panels',
      description: 'Document all exterior surfaces, sides, front, and rear',
      requiredPhotos: 4,
      icon: 'Box',
      tips: ['Capture all four sides', 'Include close-ups of any damage', 'Use good lighting']
    },
    {
      id: 'interior',
      title: 'Interior Condition',
      description: 'Capture floor, walls, and cargo area condition',
      requiredPhotos: 3,
      icon: 'Home',
      tips: ['Document floor condition', 'Capture wall and ceiling', 'Show cargo area cleanliness']
    },
    {
      id: 'hitch',
      title: 'Hitch Components',
      description: 'Inspect hitch, coupler, safety chains, and jack',
      requiredPhotos: 2,
      icon: 'Link',
      tips: ['Show hitch and coupler clearly', 'Document safety chains and jack']
    },
    {
      id: 'electrical',
      title: 'Electrical Systems',
      description: 'Check lights, wiring, and electrical connections',
      requiredPhotos: 2,
      icon: 'Zap',
      tips: ['Test and photograph all lights', 'Document wiring and connector condition']
    },
    {
      id: 'tires',
      title: 'Tire Condition',
      description: 'Document all tires and wheel condition',
      requiredPhotos: 4,
      icon: 'Circle',
      tips: ['Photograph each tire individually', 'Show tread depth and sidewall']
    }
  ];

  const handleSectionComplete = async (sectionId, photos, notes) => {
    setInspectionData(prev => ({
      ...prev,
      [sectionId]: { photos, notes, completedAt: new Date()?.toISOString() }
    }));

    if (inspectionId && photos?.length > 0) {
      try {
        const uploadedPaths = [];
        for (const photo of photos) {
          if (photo?.file && !photo?.uploaded) {
            const { data, error } = await returnInspectionService?.uploadPhoto(
              photo?.file,
              inspectionId,
              sectionId
            );
            if (!error && data) {
              uploadedPaths?.push(data?.path);
              photo.uploaded = true;
              photo.path = data?.path;
            }
          } else if (photo?.path) {
            uploadedPaths?.push(photo?.path);
          }
        }
        if (uploadedPaths?.length > 0) {
          await returnInspectionService?.updateSectionPhotos(inspectionId, sectionId, uploadedPaths);
        }
        if (notes) {
          await returnInspectionService?.updateSectionNotes(inspectionId, sectionId, notes);
        }
      } catch (error) {
        console.error('Section upload error:', error);
      }
    }
  };

  const handleAddDamageReport = async (report) => {
    const newReport = { ...report, id: Date.now() };
    const updatedReports = [...damageReports, newReport];
    setDamageReports(updatedReports);
    if (inspectionId) {
      await returnInspectionService?.updateDamageReport(inspectionId, updatedReports);
    }
  };

  const handleRemoveDamageReport = async (reportId) => {
    const updatedReports = damageReports?.filter(r => r?.id !== reportId);
    setDamageReports(updatedReports);
    if (inspectionId) {
      await returnInspectionService?.updateDamageReport(inspectionId, updatedReports);
    }
  };

  const isAllSectionsComplete = () => {
    return inspectionSections?.every(section =>
      inspectionData?.[section?.id]?.photos?.length >= section?.requiredPhotos
    );
  };

  const handleGenerateReport = () => {
    const report = returnInspectionService?.generateInspectionReport(
      inspectionData,
      inspectionSections,
      damageReports,
      gpsLocation
    );
    setGeneratedReport(report);
    setShowReport(true);
  };

  const handleSubmitInspection = async () => {
    if (!isAllSectionsComplete()) {
      toastService?.error('Please complete all inspection sections before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const report = returnInspectionService?.generateInspectionReport(
        inspectionData,
        inspectionSections,
        damageReports,
        gpsLocation
      );
      setGeneratedReport(report);

      if (inspectionId) {
        await returnInspectionService?.submitInspection(inspectionId, report, damageReports);
        if (ownerId) {
          await returnInspectionService?.notifyOwner(inspectionId, ownerId, user?.id, bookingId);
        }
      }

      toastService?.success('Return inspection submitted successfully!');
      navigate('/renter-dashboard', {
        state: { message: 'Return inspection submitted! Owner has been notified.' }
      });
    } catch (error) {
      console.error('Submission error:', error);
      toastService?.error('Failed to submit inspection. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPercentage = Math.round(
    (Object.keys(inspectionData)?.filter(key =>
      inspectionData?.[key]?.photos?.length >= (inspectionSections?.find(s => s?.id === key)?.requiredPhotos || 0)
    )?.length / inspectionSections?.length) * 100
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate('/renter-dashboard')}
                className="p-2 hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="ArrowLeft" size={20} strokeWidth={2} />
              </button>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                  Trailer Return Inspection
                </h1>
                <p className="font-body text-muted-foreground mt-1">
                  Document trailer condition to ensure transparency and prevent disputes
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-card rounded-lg p-4 shadow-elevation-2">
              <div className="flex items-center justify-between mb-2">
                <span className="font-body text-sm font-medium text-foreground">
                  Inspection Progress
                </span>
                <span className="font-body text-sm font-semibold text-primary">
                  {progressPercentage}% Complete
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Init Error */}
          {initError && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6 flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" strokeWidth={2} />
              <p className="font-body text-sm text-foreground">{initError}</p>
            </div>
          )}

          {/* GPS Location Verification */}
          <div className={`rounded-lg p-4 mb-6 flex items-start space-x-3 ${
            locationVerified
              ? 'bg-success/10 border border-success/20' :'bg-warning/10 border border-warning/20'
          }`}>
            <Icon
              name="MapPin"
              size={20}
              className={locationVerified ? 'text-success mt-0.5' : 'text-warning mt-0.5'}
              strokeWidth={2}
            />
            <div className="flex-1">
              <p className={`font-body text-sm font-medium ${locationVerified ? 'text-success' : 'text-warning'} mb-1`}>
                {locationVerified ? 'GPS Location Verified' : 'GPS Location Pending...'}
              </p>
              <p className="font-caption text-xs text-muted-foreground">
                {locationVerified
                  ? `Return location confirmed at ${new Date()?.toLocaleTimeString()} — Lat: ${gpsLocation?.latitude?.toFixed(4)}, Lng: ${gpsLocation?.longitude?.toFixed(4)}`
                  : 'Please allow location access for GPS verification'}
              </p>
            </div>
          </div>

          {/* Inspection Sections */}
          <div className="space-y-6 mb-8">
            {inspectionSections?.map((section, index) => (
              <InspectionSection
                key={section?.id}
                section={section}
                sectionNumber={index + 1}
                data={inspectionData?.[section?.id]}
                onComplete={handleSectionComplete}
                isExpanded={currentStep === index}
                onExpand={() => setCurrentStep(index)}
              />
            ))}
          </div>

          {/* Damage Report Section */}
          <div className="mb-8">
            <DamageReport
              reports={damageReports}
              onAddReport={handleAddDamageReport}
              onRemoveReport={handleRemoveDamageReport}
            />
          </div>

          {/* Comparison View */}
          <div className="mb-8">
            <ComparisonView
              inspectionData={inspectionData}
              sections={inspectionSections}
            />
          </div>

          {/* Generated Report Preview */}
          {showReport && generatedReport && (
            <div className="bg-card rounded-lg shadow-elevation-2 p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-primary" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      Inspection Report
                    </h3>
                    <p className="font-caption text-xs text-muted-foreground">
                      Report ID: {generatedReport?.reportId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReport(false)}
                  className="p-2 hover:bg-muted rounded-md transition-smooth"
                >
                  <Icon name="X" size={18} strokeWidth={2} />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="font-heading text-2xl font-bold text-primary">
                    {generatedReport?.summary?.completedSections}/{generatedReport?.summary?.totalSections}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground">Sections Done</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="font-heading text-2xl font-bold text-primary">
                    {generatedReport?.summary?.totalPhotos}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground">Photos Taken</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className="font-heading text-2xl font-bold text-warning">
                    {generatedReport?.summary?.damageReportsCount}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground">Damage Reports</p>
                </div>
                <div className="bg-muted rounded-lg p-3 text-center">
                  <p className={`font-heading text-lg font-bold ${
                    generatedReport?.overallCondition === 'Good' ? 'text-success' :
                    generatedReport?.overallCondition === 'Poor' ? 'text-error' : 'text-warning'
                  }`}>
                    {generatedReport?.overallCondition}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground">Condition</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {generatedReport?.sections?.map(section => (
                  <div key={section?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center space-x-2">
                      <Icon
                        name={section?.complete ? 'CheckCircle2' : 'AlertCircle'}
                        size={16}
                        className={section?.complete ? 'text-success' : 'text-warning'}
                        strokeWidth={2}
                      />
                      <span className="font-body text-sm text-foreground">{section?.title}</span>
                    </div>
                    <span className={`font-body text-sm font-semibold ${section?.complete ? 'text-success' : 'text-muted-foreground'}`}>
                      {section?.photosCount}/{section?.requiredPhotos} photos
                    </span>
                  </div>
                ))}
              </div>

              {generatedReport?.gpsVerification?.verified && (
                <div className="bg-success/10 border border-success/20 rounded-md p-3 flex items-center space-x-2">
                  <Icon name="MapPin" size={16} className="text-success" strokeWidth={2} />
                  <p className="font-body text-sm text-success">
                    GPS verified at {new Date(generatedReport?.gpsVerification?.timestamp)?.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Submit Section */}
          <div className="bg-card rounded-lg shadow-elevation-2 p-6">
            <div className="flex items-start space-x-3 mb-4">
              <Icon name="Info" size={20} className="text-primary mt-0.5" strokeWidth={2} />
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  Ready to Submit?
                </h3>
                <p className="font-body text-sm text-muted-foreground mb-4">
                  Please review all inspection photos and damage reports before submitting.
                  Once submitted, this inspection will be sent to the owner for review.
                </p>
                <div className="space-y-2 mb-4">
                  {inspectionSections?.map(section => {
                    const sectionData = inspectionData?.[section?.id];
                    const isComplete = (sectionData?.photos?.length || 0) >= section?.requiredPhotos;
                    return (
                      <div key={section?.id} className="flex items-center space-x-2">
                        <Icon
                          name={isComplete ? 'CheckCircle2' : 'Circle'}
                          size={16}
                          className={isComplete ? 'text-success' : 'text-muted-foreground'}
                          strokeWidth={2}
                        />
                        <span className={`font-body text-sm ${isComplete ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {section?.title} ({sectionData?.photos?.length || 0}/{section?.requiredPhotos} photos)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={handleGenerateReport}
                iconName="FileText"
                className="flex-1"
                disabled={isSubmitting}
              >
                Preview Report
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/renter-dashboard')}
                className="flex-1"
                disabled={isSubmitting}
              >
                Save as Draft
              </Button>
              <Button
                variant="default"
                onClick={handleSubmitInspection}
                iconName="Send"
                loading={isSubmitting}
                disabled={!isAllSectionsComplete() || isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inspection'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrailerReturnInspection;